import { useMemo } from 'react'
import { IModalsObject } from '../../interfaces'
import { isObject } from '../../utils/validTypes'

/*
 * Module-level so a missing list keeps the `useMemo` dependency stable: a
 * fresh `[]` or `{}` per render would recompute the list on every render.
 */
const NO_MODAL_NAMES: unknown[] = []
const NO_MODALS: IModalsObject = {}

/**
 * Pairs each modal name a container lists with that modal's config.
 *
 * A names list that is not an array — `null`, `false`, `""`, `{}` — is read
 * as no modals, and only non-empty string entries are kept as names. These
 * arrive from remote config, which nothing type-checks on the way in: a
 * container's `modals: null` survives `deepMerge` (it only merges plain
 * objects, so the value replaces cinnamon's default list). `Default` used to
 * destructure it with a `= []` default, which does not catch `null`, and pass
 * it straight here, which crashed the page into the error boundary
 * (COM-1337). Callers now pass the raw value and rely on this guard, so an
 * absent list maps to the same constant on every render.
 *
 * The modals-object fallback is **defensive**, not a live path: both callers
 * receive the top-level `config.modals` through `routes/index.tsx`, which
 * passes `config.modals || {}`. It is here so the hook's contract does not
 * rest on its callers.
 *
 * A listed name with no authored modal is kept, with `data: {}`. Whether it
 * renders is `ModalContainer`'s call, made against the modal status slice.
 * A name listed twice is kept once.
 */
export const useModalsList = (
  modalsNames: unknown,
  modals: IModalsObject | null | undefined,
) => {
  const listed: unknown[] = Array.isArray(modalsNames)
    ? modalsNames
    : NO_MODAL_NAMES
  const modalsByName = isObject(modals)
    ? (modals as IModalsObject)
    : NO_MODALS
  return useMemo(() => {
    // The templates key each modal by name, so only a non-empty string can be
    // one: `1` and `'1'` would collide as keys, and `null` or `{}` name no
    // modal anyone could author.
    const names = listed.filter(
      (name): name is string => typeof name === 'string' && name !== '',
    )
    // A repeat would be a duplicate key and would only stack a second copy of
    // the same modal, so the first occurrence wins.
    const uniqueNames = names.filter(
      (name, index) => names.indexOf(name) === index,
    )
    return uniqueNames.map((name) => ({
      name,
      data: modalsByName[name] || {},
    }))
  }, [listed, modalsByName])
}
